<script setup lang="ts">
import { computed, toRefs } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/api/index';
import { PageName } from '@/api/tracking';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import Clipboard from 'clipboard';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import { AdpDocument, isPatent, isReport } from '@/types';
import { getPageUrl, getPublisher } from '@/utils/adpDocument';
import { formatDate } from '@/utils/formatters';

interface Props {
  adpDocuments: AdpDocument[];
  pageName: PageName;
  groupId?: number;
  isExportEditableInfo?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isExportEditableInfo: true,
});

const route = useRoute();
const { createSnackbar } = useSnackbar();

const { adpDocuments } = toRefs(props);

const isGroupView = computed(() => props.groupId !== undefined);

const formatArticle = (adpDocument: AdpDocument) => {
  return [
    `${adpDocument.title}`,
    `日付： ${
      adpDocument.published_at ? formatDate(adpDocument.published_at) : ''
    }`,
    `掲載メディア： ${getPublisher(adpDocument)}`,
    `URL： ${getPageUrl(adpDocument, false)}`,
    props.isExportEditableInfo
      ? `${isGroupView.value ? 'グループメモ' : 'マイメモ'}： ${
          adpDocument.memo ?? ''
        }`
      : null,
    props.isExportEditableInfo
      ? `${isGroupView.value ? 'グループタグ' : 'マイタグ'}： ${
          adpDocument.tags?.join(',') ?? ''
        }`
      : null,
  ]
    .filter(val => val !== null)
    .join('\n');
};

const copy = () => {
  const articleData = adpDocuments.value.map(formatArticle).join('\n\n');

  const clipboard = new Clipboard('.copy-button', {
    text: () => articleData,
  });
  clipboard.on('success', e => {
    // IEではクリップボードアクセスが許可されていない場合があるため
    // クリップボードがコピーした内容と同じか確認している
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).clipboardData) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const text = (window as any).clipboardData
        .getData('text')
        .replace(/\r\n/g, '\n');
      if (e.text !== text) {
        createSnackbar({
          message: '記事情報をコピーできませんでした',
          type: 'error',
        });
        return;
      }
    }
    api.trackEvent(
      'copy_articles_to_clipboard',
      {
        pageName: props.pageName,
        pageUrl: route.fullPath,
      },
      undefined,
      props.groupId,
      {
        articles: adpDocuments.value.map(a => ({
          id: a.id,
          type: a.doc_type,
          lang: a.lang,
          patent_publication_office: isPatent(a)
            ? a.publication_office
            : undefined,
          report_page_number: isReport(a) ? a.page_number : undefined,
        })),
      },
    );
    createSnackbar({
      message: '記事情報をコピーしました',
      type: 'success',
    });
    clipboard.destroy();
  });
};
</script>

<template>
  <span class="copy-button" @click="copy">
    <DgrIcon size="small" name="copy" /><span class="c-title-bold--m"
      >記事情報をコピー</span
    >
  </span>
</template>

<style lang="scss" scoped>
.copy-button {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  background-color: #fff;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  padding: 4px 12px;
  width: 100%;
  box-sizing: border-box;
  justify-content: center;

  .c-title-bold--m {
    margin-left: 6px;
  }
}
</style>
